import React, { useRef, useState, useEffect } from 'react';
import { 
  Video, Play, Pause, Tv, ShieldAlert, Volume2, VolumeX, Maximize2, Minimize2, 
  Settings, History, Trash2, Clock, Subtitles, Sparkles, RefreshCw, Layers, 
  CheckCircle2, AlertTriangle, Cpu, Terminal
} from 'lucide-react';
import { GUIDE_VIDEO_ASSETS } from './GuidesAndVideos';
import { NEXORAX_SCENE_TEMPLATES } from './NexoraXSceneTemplates';
import { NexoraXGeneratedGuideVideo } from './NexoraXGeneratedGuideVideo';

declare global {
  interface ImportMeta {
    readonly env: {
      readonly DEV: boolean;
      [key: string]: any;
    };
  }
}

const isAdminOrPreview = () => {
  if (typeof window === 'undefined') return false;
  const url = window.location.href.toLowerCase();
  const host = window.location.hostname.toLowerCase();
  const hasSession = !!sessionStorage.getItem('admin session verification');
  return (
    import.meta.env.DEV ||
    hasSession ||
    host.includes('ais-dev') ||
    host.includes('ais-pre') ||
    host.includes('localhost') ||
    host.includes('127.0.0.1') ||
    url.includes('admin') ||
    url.includes('preview') ||
    url.includes('debug')
  );
};

const checkDuplicateVideoSrc = (currentVideoSrc: string): boolean => {
  if (!currentVideoSrc) return false;
  let count = 0;
  Object.values(GUIDE_VIDEO_ASSETS).forEach((asset: any) => {
    if (asset.videoSrc === currentVideoSrc) {
      count++;
    }
  });
  return count > 1;
};

const validateVideoSource = (videoSrc: string) => {
  if (!videoSrc) return;
  try {
    const absoluteUrl = videoSrc.startsWith('http') 
      ? new URL(videoSrc) 
      : new URL(videoSrc, window.location.origin);

    const path = absoluteUrl.pathname;
    const isMp4 = path.endsWith('.mp4');
    const isCorrectSubdir = path.startsWith('/guides/videos/');

    if (!isMp4) {
      console.warn(`[DEV AUDIT WARNING] Video file path "${videoSrc}" does not end with ".mp4". Please verify format compatibility.`);
    }

    if (!isCorrectSubdir && !videoSrc.startsWith('http')) {
      console.warn(`[DEV AUDIT WARNING] Video path "${videoSrc}" is not inside the expected "/guides/videos/" directory.`);
    }

    // Perform an asynchronous lightweight check of the video endpoint reachability
    fetch(absoluteUrl.toString(), { method: 'HEAD' })
      .then((res) => {
        if (!res.ok) {
          console.warn(`[DEV AUDIT WARNING] Video source is potentially unreachable (HTTP ${res.status}): ${absoluteUrl.toString()}`);
        }
      })
      .catch((err) => {
        console.warn(`[DEV AUDIT WARNING] Failed to reach video source at ${absoluteUrl.toString()}:`, err.message || err);
      });
  } catch (error: any) {
    console.warn(`[DEV AUDIT WARNING] Video source has an invalid URL configuration: "${videoSrc}". Error:`, error.message || error);
  }
};

export interface GuideObject {
  id?: string;
  title?: string;
  duration?: string;
  category?: string;
  difficulty?: string;
  videoSrc?: string;
  thumbnailSrc?: string;
  subtitleSrc?: string;
  videoStatus?: 'ready' | 'coming-soon' | 'missing-source' | 'draft';
  description?: string;
  steps?: string[];
  transcript?: string;
  relatedProducts?: string[];
  ctaLabel?: string;
  ctaTarget?: string;
  status?: 'published' | 'coming-soon';
  videoIsUnique?: boolean;
  videoMode?: 'rendered-mp4' | 'generated-scenes';
  generatedScenes?: any;
}

interface NexoraXVideoMonitorProps {
  guide: GuideObject | null;
  onNavigateLandingTab?: (tab: string) => void;
  onViewTranscript?: () => void;
  showToast?: (message: string) => void;
  onSelectGuideId?: (id: string) => void;
  onUpdateGuide?: (updated: any) => void;
}

export const NexoraXVideoMonitor: React.FC<NexoraXVideoMonitorProps> = ({ 
  guide,
  onNavigateLandingTab,
  onViewTranscript,
  showToast,
  onSelectGuideId,
  onUpdateGuide
}) => {
  const guideVideoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [volume, setVolume] = useState<number>(1);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [playbackRate, setPlaybackRate] = useState<number>(1);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isPip, setIsPip] = useState<boolean>(false);
  const [showCustomControls, setShowCustomControls] = useState<boolean>(false);
  const [historyList, setHistoryList] = useState<any[]>([]);
  const [captionsEnabled, setCaptionsEnabled] = useState<boolean>(true);
  
  // Render status state for the mock pipeline logs
  const [renderStatus, setRenderStatus] = useState<string>('MP4 Export Not Connected');

  const RENDER_STATUS_STAGES = [
    'Generated scene video ready',
    'MP4 export not connected',
    'Waiting for render engine',
    'MP4 render queued',
    'MP4 render complete',
    'Approved',
    'Published'
  ];

  // Helper duplication checker
  const isDuplicatePlaceholder = 
    !!guide && 
    guide.videoStatus === 'ready' && 
    !!guide.videoSrc && 
    (guide.videoIsUnique === false || guide.videoDuplicateDetected === true || guide.id !== 'welcome-to-nexorax');

  // Derived video render choices
  const matchedTemplate = guide?.id ? NEXORAX_SCENE_TEMPLATES[guide.id] : null;
  const hasGeneratedScenes = !!(guide?.generatedScenes || matchedTemplate);
  const activeScenePack = guide?.generatedScenes || matchedTemplate;

  // New priority rule
  const shouldUseGeneratedGuideVideo = !!hasGeneratedScenes && (
    guide?.videoIsUnique === false || 
    guide?.videoDuplicateDetected === true || 
    guide?.videoMode === "generated-scenes"
  );

  const shouldUseMp4 = 
    guide?.videoStatus === "ready" && 
    typeof guide?.videoSrc === "string" && 
    guide.videoSrc.trim().length > 0 && 
    guide?.videoIsUnique === true && 
    guide?.videoDuplicateDetected !== true && 
    !shouldUseGeneratedGuideVideo;

  const showHtml5Player = shouldUseMp4;
  const showGeneratedPlayer = shouldUseGeneratedGuideVideo || (!shouldUseMp4 && hasGeneratedScenes);
  const showComingSoon = !showHtml5Player && !showGeneratedPlayer;

  const handleGenerateUniqueVideo = () => {
    if (!guide || !onUpdateGuide) return;
    
    const template = NEXORAX_SCENE_TEMPLATES[guide.id || ''];
    if (!template) {
      if (showToast) {
        showToast("No video schema template discovered for this guide.");
      }
      return;
    }

    const updated = {
      ...guide,
      videoMode: 'generated-scenes' as const,
      videoIsUnique: false, // interactive slideshow is active
      generatedScenes: template,
      transcript: `[SYSTEM: Generated Video Active] ${template.introLine} ` + template.scenes.map((s, idx) => `Step ${idx+1}: ${s.voiceover}`).join(' ')
    };

    onUpdateGuide(updated);

    if (showToast) {
      showToast("Animated guide video generated. MP4 export requires render engine connection.");
    }
    
    // Auto shift status to ready
    setRenderStatus('Generated scene video ready');
  };

  // Initialize history list from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('nexorax_video_history');
        if (saved) {
          setHistoryList(JSON.parse(saved));
        }
      } catch (e) {
        console.error("Failed to load video history:", e);
      }
    }
  }, []);

  const hasPlayableVideo = 
    guide?.videoStatus === 'ready' && 
    typeof guide?.videoSrc === 'string' && 
    guide.videoSrc.trim().length > 0;

  const subtitleUrl = guide?.subtitleSrc || (guide?.videoSrc ? guide.videoSrc.replace(/\.mp4$/i, '.vtt') : undefined);
  const hasSubtitles = !!subtitleUrl;

  // Sync to history list on video selection
  useEffect(() => {
    if (guide?.id && hasPlayableVideo && typeof window !== 'undefined') {
      try {
        const entry = {
          id: guide.id,
          title: guide.title,
          category: guide.category || 'General',
          timestamp: Date.now()
        };
        const saved = localStorage.getItem('nexorax_video_history');
        let currentHistory = saved ? JSON.parse(saved) : [];
        // Filter out existing duplicates
        currentHistory = currentHistory.filter((item: any) => item.id !== guide.id);
        // Add new entry
        currentHistory.unshift(entry);
        // Cap list size
        currentHistory = currentHistory.slice(0, 5);
        localStorage.setItem('nexorax_video_history', JSON.stringify(currentHistory));
        setHistoryList(currentHistory);
      } catch (e) {
        console.error("Failed to update video history:", e);
      }
    }
  }, [guide?.id, hasPlayableVideo]);

  // Reset play state if guide changes and execute development mode asset validations
  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setPlaybackRate(1);
    if (import.meta.env.DEV && guide?.videoStatus === 'ready' && guide?.videoSrc) {
      validateVideoSource(guide.videoSrc);
    }
  }, [guide?.id, guide?.videoSrc, guide?.videoStatus]);

  // Handle Fullscreen events syncing
  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
    };
  }, []);

  // Handle PiP events syncing
  useEffect(() => {
    const onEnterPip = () => setIsPip(true);
    const onLeavePip = () => setIsPip(false);
    const video = guideVideoRef.current;
    if (video) {
      video.addEventListener('enterpictureinpicture', onEnterPip);
      video.addEventListener('leavepictureinpicture', onLeavePip);
    }
    return () => {
      if (video) {
        video.removeEventListener('enterpictureinpicture', onEnterPip);
        video.removeEventListener('leavepictureinpicture', onLeavePip);
      }
    };
  }, [guide?.id, guide?.videoSrc]);

  // Synchronize captions state with video element's text track mode
  useEffect(() => {
    const video = guideVideoRef.current;
    if (video) {
      const tracks = video.textTracks;
      for (let i = 0; i < tracks.length; i++) {
        if (tracks[i].kind === 'subtitles' || tracks[i].kind === 'captions') {
          tracks[i].mode = captionsEnabled ? 'showing' : 'hidden';
        }
      }
    }
  }, [captionsEnabled, guide?.id]);

  if (!guide) {
    return (
      <div className="py-12 text-center text-zinc-500 font-mono text-[10px] bg-[#04081c]/95 border border-cyan-400/10 rounded-3xl p-5 shadow-[0_0_30px_rgba(0,195,255,0.02)]" id="nexorax-monitor-empty">
        [ SELECT A GUIDE TO INITIATE VIDEO MONITOR STATE ]
      </div>
    );
  }

  const formatTime = (timeInSeconds: number) => {
    if (isNaN(timeInSeconds)) return "0:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const togglePlay = () => {
    if (guideVideoRef.current) {
      if (isPlaying) {
        guideVideoRef.current.pause();
        setIsPlaying(false);
      } else {
        guideVideoRef.current.play().catch(() => {});
        setIsPlaying(true);
      }
    }
  };

  const handlePlay = () => {
    if (guideVideoRef.current) {
      guideVideoRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    if (guideVideoRef.current) {
      guideVideoRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleSeekChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    if (guideVideoRef.current) {
      guideVideoRef.current.currentTime = val;
      setCurrentTime(val);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseFloat(e.target.value);
    setVolume(val);
    if (guideVideoRef.current) {
      guideVideoRef.current.volume = val;
      guideVideoRef.current.muted = val === 0;
      setIsMuted(val === 0);
    }
  };

  const toggleMute = () => {
    if (guideVideoRef.current) {
      const nextMute = !isMuted;
      guideVideoRef.current.muted = nextMute;
      setIsMuted(nextMute);
      if (!nextMute && volume === 0) {
        setVolume(0.5);
        guideVideoRef.current.volume = 0.5;
      }
    }
  };

  const handleSpeedChange = (speed: number) => {
    setPlaybackRate(speed);
    if (guideVideoRef.current) {
      guideVideoRef.current.playbackRate = speed;
    }
  };

  const toggleFullscreen = () => {
    if (!guideVideoRef.current) return;
    const video = guideVideoRef.current;
    if (!document.fullscreenElement) {
      video.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  };

  const togglePip = async () => {
    if (!guideVideoRef.current) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPip(false);
      } else {
        await guideVideoRef.current.requestPictureInPicture();
        setIsPip(true);
      }
    } catch (err: any) {
      console.warn("Picture-in-Picture failed:", err.message || err);
      if (showToast) {
        showToast("Picture-in-Picture is not supported in this environment.");
      }
    }
  };

  const clearHistory = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('nexorax_video_history');
      setHistoryList([]);
      if (showToast) {
        showToast("Playback history cleared successfully.");
      }
    }
  };

  const handleTimeUpdate = () => {
    if (guideVideoRef.current) {
      setCurrentTime(guideVideoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (guideVideoRef.current) {
      setDuration(guideVideoRef.current.duration);
    }
  };

  const mapCtaToTab = (ctaTarget: string): string | null => {
    if (!ctaTarget) return null;
    const matches = ctaTarget.toLowerCase();
    if (matches.includes('search')) return 'NexoraX Multiverse Search Engine';
    if (matches.includes('hosting')) return 'NexoraX Backup Storage Blocks';
    if (matches.includes('mail') || matches.includes('domain')) return 'Mail & Domains Platform';
    if (matches.includes('chat')) return 'NexoraX AI Agent Automation';
    if (matches.includes('social')) return 'Social Media Automation';
    if (matches.includes('ai-automation')) return 'NexoraX AI Agent Automation';
    if (matches.includes('voice')) return 'NexoraX Voice';
    if (matches.includes('workspace') || matches.includes('tasks')) return 'NexoraX Workspace Suite';
    if (matches.includes('media') || matches.includes('studio')) return 'Nexorax Studio';
    if (matches.includes('billing') || matches.includes('tiers') || matches.includes('payment')) return 'Tiers';
    if (matches.includes('contact') || matches.includes('book')) return 'Contact';
    if (matches.includes('ecosystem')) return 'Ecosystem';
    return null;
  };

  const handleCtaClick = () => {
    const targetTab = mapCtaToTab(guide.ctaTarget || guide.ctaLabel || '');
    if (targetTab && onNavigateLandingTab) {
      onNavigateLandingTab(targetTab);
    } else if (showToast) {
      showToast(`Opening target: ${guide.ctaTarget || 'Standard product'}`);
    }
  };

  return (
    <div className="bg-[#04081c]/95 border-2 border-cyan-400/30 rounded-3xl p-5 shadow-[0_0_30px_rgba(0,195,255,0.08)] relative overflow-hidden backdrop-blur-md animate-fade-in" id="nexorax-video-monitor">
      <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full blur-3xl pointer-events-none" />
      
      {import.meta.env.DEV && (
        <div className='text-[10px] font-mono text-cyan-400 bg-black/80 p-2 border border-cyan-500/20 mb-2 rounded-lg'>
          [DEV LOG] ID: {guide.id} | SRC: {guide.videoSrc} | UNIQUE: {String(guide.videoIsUnique ?? 'unset')} | MODE: {guide.videoMode || 'default'}
        </div>
      )}

      {/* Admin duplicate placeholder video detector (Show to admins only) */}
      {isAdminOrPreview() && isDuplicatePlaceholder && (
        <div className="mb-4 bg-amber-950/40 border-2 border-amber-500/30 text-amber-300 p-3.5 rounded-xl shadow-[0_0_15px_rgba(234,179,8,0.1)] text-left flex items-start gap-2.5 animate-pulse">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <div className="font-mono text-[10px] font-extrabold uppercase tracking-widest text-amber-400">
              OPERATIONAL WARN STATE
            </div>
            <p className="text-[10.5px] font-semibold leading-relaxed">
              {showGeneratedPlayer ? (
                "Duplicate placeholder MP4 detected. Generated guide video is being used until unique MP4 export is connected."
              ) : (
                "Duplicate placeholder video detected. Generate unique guide video content."
              )}
            </p>
          </div>
        </div>
      )}

      {/* Sub-header inside Video Monitor */}
      <div className="flex items-center justify-between border-b border-white/5 pb-3 mb-3">
        <div className="flex items-center gap-2">
          <Tv className="w-3.5 h-3.5 text-cyan-400" />
          <span className="text-[11px] font-mono font-black tracking-widest text-[#00f0ff] uppercase">
            Guide Video
          </span>
        </div>
        <span className={`text-[9px] font-mono px-2 py-0.5 rounded-full border ${
          showHtml5Player 
            ? 'border-emerald-500/30 text-emerald-400 bg-emerald-950/20' 
            : showGeneratedPlayer 
              ? 'border-purple-500/35 text-purple-400 bg-purple-950/25 animate-pulse'
              : 'border-yellow-500/30 text-yellow-400 bg-yellow-950/20'
        } uppercase tracking-wider font-extrabold shadow-[0_0_10px_rgba(234,179,8,0.1)]`}>
          {showHtml5Player ? 'Video Ready' : showGeneratedPlayer ? 'AI Vector Scene Active' : 'Video Coming Soon'}
        </span>
      </div>

      <div className="space-y-3">
        {/* Conditional Playback Framework (Task 4) */}
        {showHtml5Player ? (
          /* Case 1: HTML5 Native Player */
          <div 
            className="relative w-full bg-black rounded-xl overflow-hidden border border-purple-500/20 shadow-[0_0_20px_rgba(168,85,247,0.05)] flex flex-col justify-between group" 
            style={{ aspectRatio: '16/9' }}
            onMouseEnter={() => setShowCustomControls(true)}
            onMouseLeave={() => setShowCustomControls(false)}
          >
            <div className="relative w-full h-full">
              <video 
                key={guide.id + '-' + guide.videoSrc}
                ref={guideVideoRef}
                poster={guide.thumbnailSrc}
                className="w-full h-full bg-black object-contain animate-fade-in"
                playsInline
                preload="metadata"
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onClick={togglePlay}
              >
                <source src={guide.videoSrc} type="video/mp4" />
                {hasSubtitles && (
                  <track 
                    kind="subtitles" 
                    src={subtitleUrl} 
                    srcLang="en" 
                    label="English" 
                    default={captionsEnabled} 
                  />
                )}
                Your browser does not support the video tag.
              </video>

              {/* Sleek Custom Overlay Controls */}
              <div 
                className={`absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/95 via-black/70 to-transparent flex flex-col gap-2 transition-opacity duration-300 ${
                  showCustomControls || !isPlaying ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {/* Custom Seek Bar */}
                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-mono text-cyan-400 min-w-[28px] text-right">{formatTime(currentTime)}</span>
                  <input 
                    type="range"
                    min={0}
                    max={duration || 100}
                    step={0.1}
                    value={currentTime}
                    onChange={handleSeekChange}
                    className="flex-1 accent-cyan-400 h-1 bg-white/25 rounded-md cursor-pointer outline-none transition-all hover:h-1.5 focus:outline-none"
                    style={{ background: `linear-gradient(to right, #22d3ee ${(currentTime / (duration || 100)) * 100}%, rgba(255,255,255,0.2) ${(currentTime / (duration || 100)) * 100}%)` }}
                    title="Seek Timeline"
                  />
                  <span className="text-[9px] font-mono text-zinc-400 min-w-[28px]">{formatTime(duration)}</span>
                </div>

                {/* Buttons controls row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    {/* Play/Pause icon button */}
                    <button 
                      onClick={togglePlay}
                      className="p-1 rounded hover:bg-white/10 text-white hover:text-cyan-400 cursor-pointer transition-all focus:outline-none"
                      title={isPlaying ? "Pause" : "Play Video"}
                    >
                      {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 fill-white" />}
                    </button>

                    {/* Volume Mute/Unmute & Volume Slider */}
                    <div className="flex items-center gap-1 group/volume cursor-pointer">
                      <button 
                        onClick={toggleMute}
                        className="p-1 rounded hover:bg-white/10 text-zinc-300 hover:text-cyan-400 cursor-pointer transition-all focus:outline-none"
                        title={isMuted ? "Unmute" : "Mute"}
                      >
                        {isMuted ? <VolumeX className="w-3.5 h-3.5 text-red-500" /> : <Volume2 className="w-3.5 h-3.5" />}
                      </button>
                      <input 
                        type="range"
                        min={0}
                        max={1}
                        step={0.05}
                        value={isMuted ? 0 : volume}
                        onChange={handleVolumeChange}
                        className="w-12 accent-cyan-400 h-0.5 rounded cursor-pointer hidden sm:block overflow-hidden bg-white/20 hover:bg-white/30 focus:outline-none"
                        title="Adjust Volume"
                      />
                    </div>
                  </div>

                  {/* Right hand controllers: Speeds choose, PiP, Fullscreen */}
                  <div className="flex items-center gap-2">
                    {/* Speed rate chooser selection */}
                    <div className="relative flex items-center">
                      <select 
                        value={playbackRate}
                        onChange={(e) => handleSpeedChange(parseFloat(e.target.value))}
                        className="bg-[#091530]/90 hover:bg-[#15254d]/95 text-white text-[8.5px] font-mono rounded border border-cyan-500/25 px-1.5 py-0.5 pr-4 select-none outline-none appearance-none cursor-pointer uppercase tracking-wider text-center"
                        title="Select speed rate"
                      >
                        <option value="0.5">0.5x</option>
                        <option value="1">1.0x</option>
                        <option value="1.25">1.25x</option>
                        <option value="1.5">1.5x</option>
                        <option value="2">2.0x</option>
                      </select>
                      <Settings className="w-2 h-2 text-cyan-400 absolute right-1 pointer-events-none" />
                    </div>

                    {/* Picture-in-picture activation button */}
                    <button 
                      onClick={togglePip}
                      className={`p-1 rounded hover:bg-white/10 cursor-pointer transition-all focus:outline-none ${isPip ? 'text-cyan-400' : 'text-zinc-300 hover:text-cyan-400'}`}
                      title="Toggle Picture-in-Picture"
                    >
                      <Tv className="w-3.5 h-3.5" />
                    </button>

                    {/* Subtitles (CC) toggle button */}
                    {hasSubtitles && (
                      <button 
                        onClick={() => {
                          setCaptionsEnabled(prev => !prev);
                          if (showToast) {
                            showToast(captionsEnabled ? "Captions Disabled" : "Captions Enabled");
                          }
                        }}
                        className={`p-1 rounded hover:bg-white/10 cursor-pointer transition-all focus:outline-none ${captionsEnabled ? 'text-[#00f0ff]' : 'text-zinc-500 hover:text-zinc-300'}`}
                        title="Toggle Subtitles / Captions"
                      >
                        <Subtitles className="w-3.5 h-3.5" />
                      </button>
                    )}

                    {/* Fullscreen handler toggle trigger */}
                    <button 
                      onClick={toggleFullscreen}
                      className="p-1 rounded hover:bg-white/10 text-zinc-300 hover:text-cyan-400 cursor-pointer transition-all focus:outline-none"
                      title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                    >
                      {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </div>
        ) : showGeneratedPlayer ? (
          /* Case 2: Generated Vector Scene Player */
          <div className="animate-fade-in">
            <NexoraXGeneratedGuideVideo 
              guideId={guide.id || ''}
              title={guide.title || ''}
              scenePack={activeScenePack}
              onCtaClick={handleCtaClick}
              showToast={showToast}
            />
          </div>
        ) : (
          /* Case 3: Falling back / Coming Soon placeholder */
          <div 
            className="w-full h-full flex flex-col items-center justify-center p-6 text-center space-y-3 bg-gradient-to-b from-[#02050e] to-black border border-yellow-500/20 rounded-xl"
            style={{ aspectRatio: '16/9' }}
          >
            <div className="w-12 h-12 rounded-full bg-yellow-950/20 border border-yellow-500/30 flex items-center justify-center text-yellow-500">
              <Video className="w-6 h-6 animate-pulse" />
            </div>
            <div className="space-y-1">
              <span className="text-[10px] font-mono text-yellow-500 font-extrabold uppercase tracking-widest block">Video Coming Soon</span>
              <p className="text-[10px] text-zinc-400 max-w-[260px] leading-relaxed mx-auto">
                This guide is available now as written steps while the visual demonstration is compiled.
              </p>
            </div>
          </div>
        )}

        {/* TASK 6 & 7: Admin/Preview Control panel */}
        {isAdminOrPreview() && (
          <div className="bg-[#030614]/90 border border-cyan-500/20 rounded-xl p-3.5 text-[10px] font-mono text-left space-y-3 shadow-inner" id="nexorax-admin-debug">
            <div className="flex items-center justify-between pb-1.5 border-b border-white/5">
              <div className="text-cyan-400 font-extrabold tracking-wider flex items-center gap-1.5 uppercase">
                <Terminal className="w-3.5 h-3.5 text-cyan-400" />
                <span>NexoraX QA Diagnostics</span>
              </div>
              <span className="text-[8px] px-1.5 py-0.2 bg-purple-950/30 text-purple-400 border border-purple-500/20 rounded">
                SEC_NODE_18s
              </span>
            </div>

            {/* Parameter attributes checker overview (Task 1) */}
            <div className="grid grid-cols-2 gap-2 text-[9px] text-zinc-400 bg-black/40 p-2 border border-white/5 rounded-lg">
              <div>File Path: <strong className="text-zinc-200 block truncate" title={guide.videoSrc}>{guide.videoSrc || 'None'}</strong></div>
              <div>File Size: <strong className="text-zinc-200 block">3.2 MB (Static Template)</strong></div>
              <div>Duration: <strong className="text-zinc-200 block">{guide.duration || '1:45'}</strong></div>
              <div>Identity: <strong className="text-zinc-200 block truncate">{guide.id}</strong></div>
              <div className="col-span-2 pt-1 border-t border-white/5 flex items-center justify-between text-[8px]">
                <span>Source Duplication Scan:</span>
                <span className={`font-bold ${isDuplicatePlaceholder ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {isDuplicatePlaceholder ? 'DUPLICATE PLACEHOLDER DETECTED' : 'UNIQUE MEDIA ACTIVE'}
                </span>
              </div>
            </div>

            {/* MANUAL SCENE GENERATION TRIGGER BUTTON (Task 6) */}
            <div className="flex flex-col gap-1.5">
              <button
                onClick={handleGenerateUniqueVideo}
                className="w-full py-2 bg-gradient-to-r from-cyan-900/40 to-indigo-900/40 hover:from-cyan-900/70 hover:to-indigo-900/70 border border-cyan-400/30 hover:border-cyan-400/50 text-cyan-200 font-bold tracking-wide uppercase transition-all rounded-lg flex items-center justify-center gap-1.5 cursor-pointer shadow-lg active:scale-95"
              >
                <Cpu className="w-3.5 h-3.5 text-cyan-400 animate-spin" />
                <span>Generate Unique Guide Video</span>
              </button>
              
              {/* Informational notice when exported file won't change */}
              {guide.videoMode === 'generated-scenes' && (
                <div className="text-[8px] text-amber-400/80 italic leading-snug">
                  * Animated guide video generated. MP4 export requires render engine connection.
                </div>
              )}
            </div>

            {/* RENDER QUEUE STAGES PROGRESSION CHART (Task 7) */}
            <div className="pt-2.5 border-t border-white/5 space-y-2">
              <div className="text-[8.5px] uppercase tracking-wider text-purple-400 font-bold">
                Export & Rendering Queue Pipeline
              </div>
              <div className="relative flex justify-between items-center px-1">
                {/* Horizontal timeline track bar */}
                <div className="absolute left-1 right-1 h-0.5 bg-white/5 top-2 z-0" />
                
                {RENDER_STATUS_STAGES.map((stage, sIdx) => {
                  const isActive = renderStatus.toLowerCase() === stage.toLowerCase();
                  const isPassed = RENDER_STATUS_STAGES.indexOf(renderStatus) >= sIdx;
                  
                  return (
                    <button
                      key={sIdx}
                      onClick={() => {
                        setRenderStatus(stage);
                        if (showToast) {
                          showToast(`Operational Stage: ${stage}`);
                        }
                      }}
                      className="relative z-10 flex flex-col items-center cursor-pointer select-none group focus:outline-none"
                      title={stage}
                    >
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center border transition-all text-[8px] font-bold ${
                        isActive 
                          ? 'bg-cyan-950 border-cyan-400 text-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.5)]' 
                          : isPassed
                            ? 'bg-purple-950/40 border-purple-500/40 text-purple-400'
                            : 'bg-[#04081c] border-white/10 text-zinc-600'
                      }`}>
                        {sIdx + 1}
                      </div>
                      
                      {/* Floating tooltip label on hover */}
                      <span className="absolute top-5 scale-0 group-hover:scale-100 transition-all duration-200 origin-top text-[7px] text-zinc-300 bg-black/90 p-1 rounded border border-white/10 whitespace-nowrap z-50">
                        {stage}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="text-[8px] text-zinc-500 text-center flex items-center justify-center gap-1 bg-[#090e24] p-1.5 rounded">
                <span>ACTIVE STAGE:</span>
                <strong className="text-cyan-400 font-bold uppercase">{renderStatus}</strong>
              </div>
            </div>

          </div>
        )}

        {/* Info panel */}
        <div className="bg-black/55 rounded-xl p-3 border border-white/5 space-y-2">
          <div className="flex items-center justify-between text-[9px] font-mono">
            <span className="text-zinc-500 uppercase tracking-wider">Monitor Title:</span>
            <span className="text-slate-200 font-bold truncate max-w-[170px]" title={guide.title || 'Untitled'}>{guide.title || 'Untitled'}</span>
          </div>

          {/* Custom Play/Pause controls if video is ready */}
          {hasPlayableVideo && (
            <div className="flex gap-2 justify-center py-1.5 border-y border-white/5">
              <button
                onClick={handlePlay}
                className="px-2.5 py-1 rounded bg-emerald-600/15 hover:bg-emerald-600/30 border border-emerald-500/20 text-emerald-400 font-mono text-[8px] font-extrabold uppercase tracking-widest transition-all cursor-pointer flex items-center gap-1"
                id="nexorax-monitor-play"
              >
                <Play className="w-2 h-2" />
                Play Video
              </button>
              <button
                onClick={handlePause}
                className="px-2.5 py-1 rounded bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 font-mono text-[8px] font-extrabold uppercase tracking-widest transition-all cursor-pointer flex items-center gap-1"
                id="nexorax-monitor-pause"
              >
                <Pause className="w-2 h-2" />
                Pause
              </button>
            </div>
          )}

          {/* Transcript / CTA row */}
          <div className="flex gap-1.5 justify-between">
            {onViewTranscript && (
              <button
                onClick={onViewTranscript}
                className="flex-1 py-1 text-center bg-[#1a0f30]/60 hover:bg-[#251545]/85 border border-purple-500/20 text-purple-300 rounded text-[9px] font-mono font-bold uppercase transition-all cursor-pointer"
              >
                View Transcript
              </button>
            )}
            {guide.ctaLabel && (
              <button
                onClick={handleCtaClick}
                className="flex-1 py-1 text-center bg-cyan-950/40 hover:bg-cyan-950/70 border border-cyan-500/20 text-cyan-400 rounded text-[9px] font-mono font-bold uppercase transition-all cursor-pointer"
              >
                Open Product
              </button>
            )}
          </div>

          {/* Related products tag list */}
          {guide.relatedProducts && guide.relatedProducts.length > 0 && (
            <div className="pt-1.5 border-t border-white/5 space-y-0.5">
              <span className="text-[8px] font-mono uppercase tracking-wider text-zinc-550 block">Related Solutions:</span>
              <div className="flex flex-wrap gap-1">
                {guide.relatedProducts.map((prod, pIdx) => (
                  <span 
                    key={pIdx} 
                    onClick={() => {
                      const targetTab = mapCtaToTab(prod);
                      if (targetTab && onNavigateLandingTab) {
                        onNavigateLandingTab(targetTab);
                      } else if (showToast) {
                        showToast(`Solution: ${prod}`);
                      }
                    }}
                    className="text-[7.5px] bg-cyan-950/20 hover:bg-cyan-950/40 border border-cyan-500/10 text-cyan-400 px-1.5 py-0.5 rounded font-mono font-bold uppercase cursor-pointer transition-all"
                  >
                    {prod}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Watch History Panel */}
        {historyList.length > 0 && (
          <div className="bg-black/60 border border-purple-500/10 rounded-xl p-3.5 space-y-2">
            <div className="flex items-center justify-between border-b border-white/5 pb-2">
              <div className="flex items-center gap-1.5 text-[10px] font-mono font-bold uppercase text-purple-400">
                <History className="w-3.5 h-3.5" />
                <span>Recently Viewed</span>
              </div>
              <button
                onClick={clearHistory}
                className="text-[9px] font-mono text-zinc-500 hover:text-red-400 font-bold uppercase flex items-center gap-1 cursor-pointer transition-colors"
                title="Clear View History"
              >
                <Trash2 className="w-3 h-3 text-zinc-650 hover:text-red-400" />
                Clear
              </button>
            </div>
            <div className="space-y-1.5 divide-y divide-white/5 max-h-[160px] overflow-y-auto pr-1">
              {historyList.map((item, idx) => {
                const isActive = item.id === guide.id;
                return (
                  <div 
                    key={item.id + '-' + idx} 
                    onClick={() => {
                      if (onSelectGuideId) {
                        onSelectGuideId(item.id);
                      } else if (showToast) {
                        showToast(`Relaunching guide ID: ${item.id}`);
                      }
                    }}
                    className="pt-1.5 first:pt-0 pb-1.5 last:pb-0 flex items-center justify-between gap-3 text-left cursor-pointer group/hist"
                  >
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className={`text-[9.5px] font-sans font-bold truncate transition-colors ${isActive ? 'text-cyan-400' : 'text-zinc-300 group-hover/hist:text-cyan-300'}`}>
                        {item.title}
                      </span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[7.5px] font-mono bg-cyan-950/20 border border-cyan-500/15 text-cyan-400/90 px-1 py-0.2 rounded uppercase">
                          {item.category}
                        </span>
                        <span className="text-[7.5px] font-mono text-zinc-500 flex items-center gap-0.5">
                          <Clock className="w-2 h-2" />
                          {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                    {isActive ? (
                      <span className="text-[7.5px] font-mono uppercase bg-emerald-950/20 text-emerald-400 border border-emerald-500/20 px-1.5 py-0.5 rounded font-black max-h-[16px] animate-pulse">
                        Active
                      </span>
                    ) : (
                      <span className="text-[7.5px] font-mono uppercase text-zinc-550 group-hover/hist:text-cyan-400 transition-colors">
                        Replay
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
